import { useNavigate } from "react-router";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useStaffContext } from "./useStaffContext";
import { useVenue } from "./VenueContext";

/**
 * Venue switcher fed by the single bootstrap query. Changing it navigates to `/v/<id>` (a full
 * remount of the venue shell → a fresh SSE subscription + query cache for the new venue); it is
 * deep-linkable and multi-tab safe because the venue lives in the URL, not global state. A native
 * `<select>` keeps it keyboard- and screen-reader-accessible (RGAA baseline) with no custom widget.
 */
export function VenueSwitcher() {
  const { venues } = useStaffContext();
  const { venueId } = useVenue();
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (venues.length <= 1) {
    const only = venues[0];
    return only ? (
      <span className={css({ fontWeight: "semibold" })}>{only.displayName}</span>
    ) : null;
  }

  return (
    <label className={css({ display: "inline-flex", alignItems: "center", gap: "2" })}>
      <span className={css({ srOnly: true })}>{t("shell.switcher.label")}</span>
      <select
        value={venueId}
        onChange={(e) => navigate(`/v/${e.target.value}`)}
        className={css({
          borderWidth: "1px",
          borderColor: "border.default",
          rounded: "md",
          px: "2",
          py: "1",
          fontSize: "sm",
          bg: "bg.default",
        })}
      >
        {venues.map((v) => (
          <option key={v.id} value={v.id}>
            {v.displayName}
          </option>
        ))}
      </select>
    </label>
  );
}
